import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Button } from "./ui/button";
import { Edit,  Trash2, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronDown, X, User, Calendar, DollarSign } from "lucide-react";
import moment from "moment-timezone";
import { isDateTimeInRange, parseAppDateTime } from "../utils/timezone";
import { useState, useMemo } from "react";
import { EmployeeDetailsContent } from "../components/EmployeeDetailsContent";
import { getTipCommissionLabel, TIP_ROLES } from "../utils/tipCommission";

export const WaitressTipsTransactionTable = ({
  records,
  currentPage,
  setCurrentPage,
  entriesPerPage,
  setEntriesPerPage,
  onEdit,
  onDelete,
  currentUser,
  dateRange ,
  employeeApiService,
  employees
}) => {
  // State for sorting and modal
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
  const [showModal, setShowModal] = useState(false);
  const [modalEmployee, setModalEmployee] = useState(null);
  const [modalRecipient, setModalRecipient] = useState(null);

  // State for employee details modal
  const [loadingEmployeeDetails, setLoadingEmployeeDetails] = useState(false);
  const [employeeDetailsError, setEmployeeDetailsError] = useState(null);
  const [showEmployeeDetailsModal, setShowEmployeeDetailsModal] = useState(false);
  const [employeeDetails, setEmployeeDetails] = useState(null);


  // Filter records by date range
  const filteredRecords = useMemo(() => {
    if (!dateRange || !dateRange.start || !dateRange.end) {
      return records;
    }

    return records.filter(record =>
      isDateTimeInRange(record.date, dateRange.start, dateRange.end)
    );
  }, [records, dateRange]);

  // Sort records
  const sortedRecords = useMemo(() => {
    if (!sortConfig.key) {
      return filteredRecords;
    }

    return [...filteredRecords].sort((a, b) => {
      let aValue = a[sortConfig.key] || '';
      let bValue = b[sortConfig.key] || '';

      // Handle date sorting
      if (sortConfig.key === 'date') {
        aValue = parseAppDateTime(aValue)?.valueOf() ?? 0;
        bValue = parseAppDateTime(bValue)?.valueOf() ?? 0;
      }

      // Handle numeric sorting for amounts
      if (['tip_out_cash', 'tax', 'owe_to_toast'].includes(sortConfig.key)) {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
  }, [filteredRecords, sortConfig]);

  // Calculate pagination values
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentEntries = sortedRecords.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(sortedRecords.length / entriesPerPage);

  // Handle employee name click
  const handleEmployeeNameClick = (employeeName, e) => {
    e.stopPropagation();
    setModalEmployee(employeeName);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setModalEmployee(null);
  };


  const handleRecipientNameClick = async (recipientName, e) => {
    e.stopPropagation();

    // Find employee by name
    const employee = employees.find(emp => emp.name === recipientName);

    if (employee && employeeApiService) {
      // Show employee details modal
      setLoadingEmployeeDetails(true);
      setEmployeeDetailsError(null);
      setShowEmployeeDetailsModal(true);

      try {
        const startDate = dateRange?.start ? moment(dateRange.start).format('YYYY-MM-DD') : moment().subtract(30, 'days').format('YYYY-MM-DD');
        const endDate = dateRange?.end ? moment(dateRange.end).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');

        const [completeDataResponse, performanceResponse] = await Promise.all([
          employeeApiService.getCompleteEmployeeData(employee.id, { startDate, endDate }),
          employeeApiService.getEmployeePerformanceMetrics(employee.id, { startDate, endDate })
        ]);

        if (completeDataResponse.success && performanceResponse.success) {
          setEmployeeDetails({
            employee: employee,
            completeData: completeDataResponse?.data?.body?.data,
            performanceMetrics: performanceResponse?.data?.body?.data
          });
        } else {
          setEmployeeDetailsError('Failed to load employee details');
        }
      } catch (error) {
        console.error('Error loading employee details:', error);
        setEmployeeDetailsError('Error loading employee details');
      } finally {
        setLoadingEmployeeDetails(false);
      }
    } else {
      // Fallback to original recipient modal
      setModalRecipient(recipientName);
      setShowEmployeeDetailsModal(true);
    }
  };

  const closeEmployeeDetailsModal = () => {
    setShowEmployeeDetailsModal(false);
    setEmployeeDetails(null);
    setEmployeeDetailsError(null);
  };

  // Request sort
  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  // Format number with commas
  const formatNumberWithCommas = (amount) => {
    const numericAmount = parseFloat(amount);
    return numericAmount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  // Format date
  const formatDate = (dateString) => {
    return parseAppDateTime(dateString)?.format("MM/DD/YYYY hh:mm A") ?? dateString;
  };

  // Generate pagination buttons
  const renderPaginationButtons = () => {
    const buttons = [];
    let startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, startPage + 4);

    if (endPage - startPage < 4) {
      startPage = Math.max(1, endPage - 4);
    }

    for (let i = startPage; i <= endPage; i++) {
      buttons.push(
        <Button
          key={i}
          variant={currentPage === i ? "default" : "outline"}
          size="icon"
          className="h-8 w-8"
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </Button>
      );
    }

    return buttons;
  };

  // Render sort icon
  const renderSortIcon = (column) => {
    if (sortConfig.key === column) {
      return sortConfig.direction === "ascending" ?
        <ChevronDown className="inline-block ml-1 h-4 w-4 rotate-180" /> :
        <ChevronDown className="inline-block ml-1 h-4 w-4" />;
    }
    return <ChevronDown className="inline-block ml-1 h-4 w-4 opacity-30" />;
  };

  // Get employee statistics for modal (filtered by date range)
  const getEmployeeStats = (employeeName) => {
    const employeeRecords = filteredRecords.filter(r => r.added_by === employeeName);
    const totalRecords = employeeRecords.length;
    const totalTipsReceived = employeeRecords.reduce((sum, r) => sum + parseFloat(r.tip_out_cash || 0), 0);
    const totalCommission = employeeRecords.reduce((sum, r) => sum + parseFloat(r.tax || 0), 0);
    const totalOweToToast = employeeRecords.reduce((sum, r) => sum + parseFloat(r.owe_to_toast || 0), 0);
    const totalPaidOut = totalTipsReceived - totalCommission;
    const latestRecord = employeeRecords.length > 0 ?
      employeeRecords.sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())[0] : null;

    return {
      totalRecords,
      totalTipsReceived,
      totalCommission,
      totalOweToToast,
      totalPaidOut,
      latestRecord
    };
  };

  // Check if user can edit/delete
  const canEditDelete = ["Admin", "Management", "Cashier", "Floor Supervisor", "Manager"].includes(currentUser?.role);

  return (
    <>
      <div className="bg-white rounded-md shadow overflow-hidden">
        {/* Show date range info if applied */}
        {dateRange && dateRange.start && dateRange.end && (
          <div className="bg-blue-50 border-b border-blue-200 px-4 py-2">
            <p className="text-sm text-blue-800">
              <Calendar className="inline h-4 w-4 mr-1" />
              Showing waitress tips from {moment(dateRange.start).format("MM/DD/YYYY")} to {moment(dateRange.end).format("MM/DD/YYYY")}
              <span className="ml-2 text-blue-600">({filteredRecords.length} total records)</span>
            </p>
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="cursor-pointer" onClick={() => requestSort("date")}>
                Date
                {renderSortIcon("date")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("employee")}>
                Employee
                {renderSortIcon("employee")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("tip_out_cash")}>
                Tips Received
                {renderSortIcon("tip_out_cash")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("tax")}>
                {getTipCommissionLabel(TIP_ROLES.WAITRESS, dateRange?.end || new Date())}
                {renderSortIcon("tax")}
              </TableHead>
              <TableHead>
                Paid Out
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("owe_to_toast")}>
                Owe To Toast
                {renderSortIcon("owe_to_toast")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("added_by")}>
                Added By
                {renderSortIcon("added_by")}
              </TableHead>
              <TableHead className="cursor-pointer" onClick={() => requestSort("updated_by")}>
                Updated By
                {renderSortIcon("updated_by")}
              </TableHead>
              {canEditDelete && <TableHead>Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentEntries.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEditDelete ? 9 : 8} className="text-center py-8 text-gray-500">
                  {filteredRecords.length === 0 ? "No records found for the selected date range" : "No records found"}
                </TableCell>
              </TableRow>
            ) : (
              currentEntries.map((record) => {
                const paidOut = parseFloat(record.tip_out_cash || 0) - parseFloat(record.tax || 0);
                return (
                  <TableRow key={record.id}>
                    <TableCell>
                      {formatDate(record.date)}
                    </TableCell>
                    <TableCell className="font-medium text-blue-700">

                    <button
                      className="font-medium text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                      onClick={(e) => handleRecipientNameClick(record.employee, e)}
                    >
                      {record.employee || '-'}
                      </button>
                    </TableCell>
                    <TableCell className="font-semibold">
                      ${formatNumberWithCommas(record.tip_out_cash)}
                    </TableCell>
                    <TableCell className="text-red-500 font-semibold">
                      -${formatNumberWithCommas(record.tax)}
                    </TableCell>
                    <TableCell className="text-green-500 font-semibold">
                      ${formatNumberWithCommas(paidOut)}
                    </TableCell>
                    <TableCell className="text-purple-500 font-semibold">
                      ${formatNumberWithCommas(record.owe_to_toast)}
                    </TableCell>
                    <TableCell>
                      <button
                        className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        onClick={(e) => handleEmployeeNameClick(record.added_by || 'Unknown', e)}
                      >
                        {record.added_by || '-'}
                      </button>
                    </TableCell>
                    <TableCell>
                      {record.updated_by ? (
                        <button
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                          onClick={(e) => handleEmployeeNameClick(record.updated_by, e)}
                        >
                          {record.updated_by}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    {canEditDelete && (
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-blue-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEdit(record);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-600"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(record);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination Controls */}
        <div className="flex justify-between items-center mt-4 text-sm px-4 py-2 border-t">
          <div>
            Showing {sortedRecords.length > 0 ? indexOfFirstEntry + 1 : 0} to{" "}
            {Math.min(indexOfLastEntry, sortedRecords.length)} of {sortedRecords.length} entries
          </div>

          <div className="flex items-center gap-2">
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded px-2 py-1"
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
            </select>
            <span>entries per page</span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            {renderPaginationButtons()}

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Employee Details Modal */}
      {showModal && modalEmployee && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">{modalEmployee}</h2>
                  <p className="text-sm text-gray-500">
                    Employee Waitress Tips Details
                    {dateRange && dateRange.start && dateRange.end && (
                      <span className="ml-2">
                        ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Statistics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Total Records</p>
                      <p className="text-xl font-bold text-blue-800">
                        {getEmployeeStats(modalEmployee).totalRecords}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-green-600 font-medium">Tips Received</p>
                      <p className="text-xl font-bold text-green-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalTipsReceived)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm text-red-600 font-medium">Tax</p>
                      <p className="text-xl font-bold text-red-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalCommission)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-emerald-600" />
                    </div>
                    <div>
                      <p className="text-sm text-emerald-600 font-medium">Paid Out</p>
                      <p className="text-xl font-bold text-emerald-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalPaidOut)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <DollarSign className="h-4 w-4 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">Owe To Toast</p>
                      <p className="text-xl font-bold text-purple-800">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).totalOweToToast)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Latest Record Card */}
              {getEmployeeStats(modalEmployee).latestRecord && (
                <div className="bg-gray-50 p-4 rounded-lg border mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-gray-800">Latest Record</h3>
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Date</p>
                      <p className="font-medium">
                        {formatDate(getEmployeeStats(modalEmployee).latestRecord.date)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Employee</p>
                      <p className="font-medium text-blue-600">
                        {getEmployeeStats(modalEmployee).latestRecord.employee || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tips Received</p>
                      <p className="font-medium text-green-600">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).latestRecord.tip_out_cash)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Tax</p>
                      <p className="font-medium text-red-600">
                        -${formatNumberWithCommas(getEmployeeStats(modalEmployee).latestRecord.tax)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Owe To Toast</p>
                      <p className="font-medium text-purple-600">
                        ${formatNumberWithCommas(getEmployeeStats(modalEmployee).latestRecord.owe_to_toast)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Records Table */}
              <div className="bg-white rounded-lg border">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-semibold text-gray-800">Recent Records</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tips Received</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tax</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Out</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Owe To Toast</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredRecords
                        .filter(r => r.added_by === modalEmployee)
                        .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
                        .slice(0, 10)
                        .map((record, index) => {
                          const paidOut = parseFloat(record.tip_out_cash || 0) - parseFloat(record.tax || 0);
                          return (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">
                                {formatDate(record.date)}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-blue-600">
                                {record.employee || '-'}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-green-600">
                                ${formatNumberWithCommas(record.tip_out_cash)}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-red-600">
                                -${formatNumberWithCommas(record.tax)}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-emerald-600">
                                ${formatNumberWithCommas(paidOut)}
                              </td>
                              <td className="px-4 py-3 text-sm font-semibold text-purple-600">
                                ${formatNumberWithCommas(record.owe_to_toast)}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button onClick={closeModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}


      {/* Employee Details*/}
      {showEmployeeDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {employeeDetails?.employee?.name || modalRecipient}-(
                    <span>{(employeeDetails?.employee?.role || '')}</span>)
                  </h2>
                  <p className="text-sm text-gray-500">
                    Employee Details
                    {dateRange && dateRange.start && dateRange.end && (
                      <span className="ml-2">
                        ({moment(dateRange.start).format("MM/DD/YYYY")} - {moment(dateRange.end).format("MM/DD/YYYY")})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={closeEmployeeDetailsModal}
                className="h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="p-6">
              {loadingEmployeeDetails ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-blue-500"></div>
                </div>
              ) : employeeDetailsError ? (
                <div className="text-center py-8 text-red-600">
                  {employeeDetailsError}
                </div>
              ) : employeeDetails ? (
                <>
                  
                                    <EmployeeDetailsContent employeeDetails={employeeDetails} />
                                
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <User className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No Details Found</h3>
                  <p className="text-gray-500 text-center max-w-md">
                    No employee details are available for the selected period. 
                    Please check if the employee has any recorded activity during this time.
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
              <Button onClick={closeEmployeeDetailsModal}>
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};